import React, { useEffect, useMemo, useState } from "react";
import { accessApi } from "../../services/accessApi";

function Badge({ children, tone = "gray" }) {
  const tones = {
    green:
      "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900",
    red:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900",
    gray:
      "bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:border-neutral-700",
  };

  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${tones[tone]}`}>
      {children}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 shadow-sm">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-bold text-black dark:text-white">{value}</div>
    </div>
  );
}

export default function ITRolePolicyMappingsPage() {
  const [rows, setRows] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const [mappingsRes, policiesRes] = await Promise.all([
        accessApi.getHrRoleMappings(),
        accessApi.getPolicies(),
      ]);

      const mappingRows = mappingsRes?.data?.data || [];
      const policiesRows = policiesRes?.data?.data || [];

      setRows(mappingRows);
      setPolicies(policiesRows);

      const nextDrafts = {};
for (const row of mappingRows) {
  nextDrafts[row.role_key] = "";
}
setDrafts(nextDrafts);
    } catch (err) {
      console.error("ITRolePolicyMappingsPage load error:", err);
      setError(err?.response?.data?.message || err.message || "Failed to load Role → Policy Mappings.");
    } finally {
      setLoading(false);
    }
  };
  const addPolicyToRole = async (roleKey) => {
  const policyCode = drafts[roleKey];
  if (!policyCode) return;

  try {
    setSavingKey(`add:${roleKey}`);
    await accessApi.createHrRolePolicyMapping({
      hr_role_key: roleKey,
      policy_code: policyCode,
    });
    await load();
  } catch (err) {
    console.error("addPolicyToRole error:", err);
    alert(err?.response?.data?.message || err.message || "Failed to add mapping.");
  } finally {
    setSavingKey("");
  }
};

const removeMapping = async (mappingId) => {
  if (!window.confirm("Remove this policy from Role?")) return;

  try {
    setSavingKey(`remove:${mappingId}`);
    await accessApi.deleteHrRolePolicyMapping(mappingId, {
      reason: "Removed from Role → Policy Mapping screen",
    });
    await load();
  } catch (err) {
    console.error("removeMapping error:", err);
    alert(err?.response?.data?.message || err.message || "Failed to remove mapping.");
  } finally {
    setSavingKey("");
  }
};

  const filteredRows = useMemo(() => {
  const needle = q.trim().toLowerCase();
  if (!needle) return rows;

  return rows.filter((row) =>
    [
      row.role_key,
      row.position_titles,
      ...(row.policies || []).map((p) => `${p.policy_code} ${p.policy_name || ""}`),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(needle)
  );
}, [rows, q]);

  const stats = useMemo(() => {
    const total = rows.length;
    const mapped = rows.filter((r) => Number(r.is_mapped) === 1).length;
    const unmapped = total - mapped;
    return { total, mapped, unmapped };
  }, [rows]);

  

  if (loading) return <div className="p-6 text-black dark:text-white">Loading Role → Policy Mappings...</div>;
  if (error) return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-black dark:text-white">Role → Policy Mapping</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Map position roles to system policies.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Total roles" value={stats.total} />
        <StatCard label="Mapped" value={stats.mapped} />
        <StatCard label="Unmapped" value={stats.unmapped} />
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by role, policy or position..."
          className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white outline-none"
        />
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Role</th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Status</th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Positions Count</th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Example Positions</th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Mapped Policy</th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.role_key}
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition"
                >
                  <td className="p-3 text-black dark:text-white font-medium">{row.role_key}</td>
                  <td className="p-3">
                    {Number(row.is_mapped) === 1 ? (
                      <Badge tone="green">Mapped</Badge>
                    ) : (
                      <Badge tone="red">Unmapped</Badge>
                    )}
                  </td>
                  <td className="p-3 text-black dark:text-white">{row.positions_count || 0}</td>
                  <td className="p-3 text-gray-700 dark:text-gray-300">{row.position_titles || "-"}</td>
                  <td className="p-3">
  <div className="flex flex-wrap gap-2 mb-3">
    {(row.policies || []).map((p) => (
      <div
        key={p.id}
        className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900"
      >
        <span>{p.policy_code}</span>
        <button
          onClick={() => removeMapping(p.id)}
          className="text-red-600 dark:text-red-300"
        >
          ×
        </button>
      </div>
    ))}
    {!row.policies?.length && <Badge tone="red">No policies</Badge>}
  </div>

  <div className="flex gap-2">
    <select
      value={drafts[row.role_key] || ""}
      onChange={(e) =>
        setDrafts((prev) => ({ ...prev, [row.role_key]: e.target.value }))
      }
      className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white outline-none"
    >
      <option value="">Select policy...</option>
      {policies.map((p) => (
        <option key={p.code} value={p.code}>
          {p.code}
        </option>
      ))}
    </select>

    <button
      onClick={() => addPolicyToRole(row.role_key)}
      disabled={!drafts[row.role_key] || savingKey === `add:${row.role_key}`}
      className="px-3 py-2 rounded-xl bg-black text-white disabled:opacity-50"
    >
      {savingKey === `add:${row.role_key}` ? "Adding..." : "Add"}
    </button>
  </div>
</td>
                  <td className="p-3">
                    
                  </td>
                </tr>
              ))}

              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    No Roles found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}